import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Card, CardContent } from '@material-ui/core';
import ctx from 'classnames';
import { StarRating } from '../StarRating';
import { App } from 'models/app';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';
import { generateRoute } from '../../utils/location';
import { canShowStarRating } from '../../component-helpers/app-reviews';
import { useRatingsAndReviews } from '../../contexts/providers/ratings-reviews';
import { truncateString } from '../../utils/helpers';
import { APP_NAME_MAX_LENGTH } from '../../utils/constants';
import { AppMarketPageIdentifier } from '../../AppRoutes';
import { getLocalizedAppDetails } from '../../component-helpers/app-details';
import { AppIcon } from '../Common/AppIcon';

interface TileProps {
  align?: 'left' | 'right';
  app: App;
  className?: string;
}

export const AppTile: React.FC<TileProps> = ({ align = '', app, className = '' }) => {
  const ratingsAndReviewsContext = useRatingsAndReviews();
  const { id, aggregateRating, developer } = app;
  const { i18n: { language }, t } = useTranslation();
  let { name, filenameIconSmall } = useMemo(() => getLocalizedAppDetails(app, language), [app, language]);
  const canShowRatings = canShowStarRating(id, developer.id, aggregateRating.reviewCount, ratingsAndReviewsContext);
  const alignClassName = align === 'left' ? styles.left : align === 'right' ? styles.right : '';
  return (
    <Card key={id} className={ctx(className, styles.tile, alignClassName)} elevation={0}>
      <RouterLink className={styles.appIconLink} to={generateRoute(AppMarketPageIdentifier.AppDetails, id)} aria-hidden={true} tabIndex={-1}>
        <AppIcon src={filenameIconSmall} />
      </RouterLink>

      <CardContent className={ctx(styles.cardContent, alignClassName)}>
        <div className={styles.itemSelection}>
          <p>
            <strong>
              <RouterLink
                className={styles.appName}
                to={generateRoute(AppMarketPageIdentifier.AppDetails, id)}
                aria-label={name}
              >
                {truncateString(name, APP_NAME_MAX_LENGTH)}
              </RouterLink>
            </strong>
          </p>
        </div>
        <div className={styles.itemSelection}>
          <p className='captionText'>
            <small>
              <RouterLink
                className={styles.developerName}
                to={generateRoute(AppMarketPageIdentifier.DeveloperApps, developer.id)}
                aria-label={t('aria.developer_name', { developerName: developer.name })}
                role='link'
                tabIndex={0}
              >
                {t('by_developer')}
                {developer.name}
              </RouterLink>
            </small>
          </p>
        </div>
        {canShowRatings ? (
          <StarRating
            rating={aggregateRating}
            size={'small'}
          />
        ) : (
          ''
        )}
      </CardContent>
    </Card>
  );
};
